import * as THREE from 'three';

/**
 * Vertical window within which two entities are considered "engageable" —
 * roughly the same level, not on two different floors of this block. Every
 * distance-based combat check (hit-testing, auto-aim targeting, an enemy's
 * own aggro/attack-range check) must respect this, not just horizontal (XZ)
 * proximity.
 *
 * History: the FIRST cut of hit-testing used full 3D `Vector3.distanceTo`,
 * which made bullets (flying at chest height) never register a hit against
 * a ground-level enemy at all — fixed to horizontal-only (`xzDistance`).
 * That fix over-corrected: with NO vertical bound whatsoever, a player
 * standing atop the fire-escape ramp (S01's own headline traversal
 * feature — the trash-can lobber's spawn point sits directly under it,
 * ~8m below at ramp height) could auto-lock, shoot, and get shot by an
 * enemy on a completely different floor, invisible and out of any sane
 * line of sight. Confirmed by S02's own 3-lens review.
 *
 * 2.5m comfortably covers the muzzle-height/feet-height offset between a
 * standing entity and a fired projectile (~1-1.5m) plus normal terrain
 * slop, while excluding every genuinely different floor level this block
 * has (ramp/rooftop/tunnel/subway are all >2.5m apart from street level at
 * the points that matter).
 */
export const VERTICAL_ENGAGEMENT_RANGE = 2.5;

/** Horizontal (XZ) distance only. */
export function xzDistance(a: THREE.Vector3, b: THREE.Vector3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** True if `a` and `b` are within `horizRadius` horizontally AND within the shared vertical engagement window. */
export function withinEngagementRange(a: THREE.Vector3, b: THREE.Vector3, horizRadius: number): boolean {
  if (Math.abs(a.y - b.y) > VERTICAL_ENGAGEMENT_RANGE) return false;
  return xzDistance(a, b) <= horizRadius;
}
